import { Fragment, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine, Legend,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
} from 'recharts';
import {
  getCareerOverview, getUserTrackVehicles, getUserVehicles,
  getTrackProgression, getTrackCornerGaps, getTrackFieldSummary,
  getTrackCornerTechnique, getBenchmarkLap, getTrackCraft, searchUsers,
  getTrackCorners, getTrackCornerProgression, getStyleTrend,
} from '../../services/api';
import { convertWeatherUnits } from '../../utils/unitConvert';
import { AXIS_INFO, ordinal } from '../SimilarityLab/DriverRadar';
import RangeSlider from '../common/RangeSlider';
import { useTheme } from '../../contexts/ThemeContext';
import styles from './ProgressPage.module.css';

function fmtLap(secs) {
  if (secs == null) return '—';
  const m = Math.floor(secs / 60);
  const s = secs - m * 60;
  return `${m}:${s.toFixed(3).padStart(6, '0')}`;
}

function fmtDate(s) {
  if (!s) return null;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function gapClass(gap) {
  // gap = field − own. Positive (field faster) = you're losing → warn.
  if (gap == null) return '';
  if (gap >= 1.5) return styles.gapBad;
  if (gap >= 0.4) return styles.gapWarn;
  return styles.gapGood;
}

// Plain-language explanations for the trend charts, condensed from the
// leaderboard column tooltips so both views describe the data the same way.
// Shown as a hover (ⓘ) next to each chart title — many users aren't sure what
// the metrics mean.
const TIPS = {
  bestScore:
    'Best lap (blue): your fastest clean lap that session — lower is better. ' +
    'Typical lap (grey dashed): the median of the session’s clean laps — the gap ' +
    'between the two lines is a consistency read. ' +
    'Driver score (green, 0–100): how much of the session’s available grip you used ' +
    'while grip was there to use — average combined G over the lap’s active samples ' +
    '(cornering, braking, coasting) ÷ the session’s demonstrated grip limit. Coasting ' +
    'counts as ~zero grip, so wasted time lowers it. 100 = at the limit whenever the tires had something to give.',
  style:
    'Grip usage (0–100, higher better): share of the session’s available grip you used while it was there to use. ' +
    'Traction (%, higher better): average traction-circle utilization — average combined G ÷ peak combined G, ' +
    'so higher = more consistently near the limit, not just in one spot. ' +
    'Smoothness (index, LOWER is better): spread of your lateral-G inputs normalized by peak grip — ' +
    'smooth, consistent steering scores lower than jerky or reactive corrections.',
  coasting:
    'Percentage of the lap spent coasting — off the throttle but not braking and not cornering ' +
    '(lateral G under ~0.25 g) while still moving: straight-line time with neither pedal doing real work. ' +
    'Lower means crisper brake-to-throttle transitions. Hover a point to also see it in seconds.',
  grip:
    'Peak G per session (99.5th-percentile, robust to sensor spikes). ' +
    'Combined G: the outermost point on the traction circle, √(latG² + lonG²). ' +
    'Braking G: the hardest deceleration. Lateral G: peak mid-corner cornering grip. ' +
    'Higher = closer to the tire’s grip limit.',
  consistency:
    'Lap-time spread — the standard deviation (σ) of your clean lap times that session. ' +
    'Lower = more repeatable, tighter laps.',
  speed:
    'Top speed: the highest speed reached in a lap. Avg speed: the lap’s average — ' +
    'lap distance ÷ lap time, so it tracks lap time directly. Under “Typical” each is ' +
    'the median across the session’s clean laps; under “Best” it’s the fastest lap’s value.',
  cornerMin:
    'Your minimum speed through this corner each session (mph). Higher = carrying more speed through the corner.',
  cornerBrake:
    'How far before the apex you got on the brakes (m). Bigger = braking earlier; smaller = braking later/deeper.',
  cornerThrottle:
    'How far after the apex you got back to power (m). Smaller = back on the throttle sooner.',
  styleRadar:
    'Your eight technique percentiles as one shape, played over time. Each axis is your ' +
    'rank (0–100) against every driver + car at this track, all time — a bigger polygon ' +
    'means more of every trait (axes where less is more, like coasting, are already ' +
    'flipped). The grey dashed outline is your average shape over the visible period. ' +
    'Axes — Braking: peak brake force vs your session’s grip ceiling. Trail: brake ' +
    'carried past turn-in toward the apex. Overlap: no grip “dead spot” between braking ' +
    'and cornering. vMin: minimum corner speed, ranked corner-by-corner. Throttle: little ' +
    'coasting between brake release and throttle. Consistency: how tightly passes repeat ' +
    'your own pattern. Smoothness: steering fluidity for the grip level (needs yaw data). ' +
    'Rotation: oversteer bias (needs yaw data). ' +
    'Modes: Session = one frame per session (noisier); Form = trailing 5-session average ' +
    '(recent form); To date = everything up to that date (accumulated fingerprint). ' +
    'The Period slider bounds the playable range; weather/temperature/Typical–Best filters don’t apply.',
};

// Recharts follows the cursor by default. When the user's edge-pinning
// preference is enabled, keep the tooltip in a fixed slot opposite the hover
// point. A callback ref ensures charts that mount after async data loads are
// measured immediately, and ResizeObserver keeps the slot correct in tile and
// stacked layouts.
function useChartEdgeTooltip(enabled, tooltipWidth = 190) {
  const observerRef = useRef(null);
  const [width, setWidth] = useState(0);
  const [side, setSide] = useState('right');

  const containerRef = useCallback(node => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!node) return;
    const updateWidth = () => setWidth(Math.round(node.clientWidth));
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(node);
    observerRef.current = observer;
  }, []);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  const onMouseMove = useCallback(event => {
    if (!enabled || !width || !event?.activeCoordinate) return;
    const fraction = event.activeCoordinate.x / width;
    if (fraction < 0.45) setSide('right');
    else if (fraction > 0.55) setSide('left');
  }, [enabled, width]);

  return {
    containerRef,
    onMouseMove,
    position: enabled && width ? {
      x: side === 'left' ? 10 : Math.max(10, width - tooltipWidth - 10),
      y: 10,
    } : undefined,
    wrapperStyle: enabled ? { width: tooltipWidth, pointerEvents: 'none' } : undefined,
  };
}

export default function ProgressPage({
  onBack, onLoadSession, onCompareSessions, currentUser,
  initialParams, onParamsChange, unitPrefs,
  onGoToLeaderboard, onGoToChannelStats, onGoToSearch, onGoToSimLab, onOpenUserSettings, onLogout, driverProfile,
}) {
  // A deep-linked/restored track (?view=progress&track=…) lands on the track
  // detail; the bare progress URL lands on the career rollup.
  const { chartColors } = useTheme();
  const pinTooltips = !!unitPrefs?.anchoredTooltip;
  const mainTooltip = useChartEdgeTooltip(pinTooltips, 220);
  const [tab, setTab] = useState(initialParams?.tab || (initialParams?.trackId ? 'track' : 'career'));
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState(null);

  // Career-wide car selector: every car the driver has driven (any track), and
  // the one currently scoping the career rollup (null = all cars). The choice
  // carries into a track's view when a track is opened.
  const [allVehicles, setAllVehicles] = useState([]);
  const [careerVehicleId, setCareerVehicleId] = useState(initialParams?.careerVehicleId ?? null);

  // ── Admin: view any driver's Progress ────────────────────────────────────
  const isAdmin = !!currentUser?.is_admin;
  const [adminMode, setAdminMode] = useState(!!(isAdmin && initialParams?.adminMode));
  const [viewUser, setViewUser] = useState(isAdmin ? (initialParams?.viewUser || null) : null);   // {id, name} | null (= self)
  const [driverQuery, setDriverQuery] = useState('');
  const [driverResults, setDriverResults] = useState([]);
  const viewUserId = viewUser?.id || null;

  // Debounced driver search (reuses the sharing user-search endpoint).
  useEffect(() => {
    if (!adminMode || driverQuery.trim().length < 2) { setDriverResults([]); return; }
    let cancelled = false;
    const t = setTimeout(() => {
      searchUsers(driverQuery.trim())
        .then(rs => !cancelled && setDriverResults(rs))
        .catch(() => !cancelled && setDriverResults([]));
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [adminMode, driverQuery]);

  // Track-tab state
  const [trackId, setTrackId] = useState(initialParams?.trackId || null);
  const [vehicles, setVehicles] = useState([]);   // the user's cars at this track
  const [vehicleId, setVehicleId] = useState(initialParams?.vehicleId ?? null);
  const [progression, setProgression] = useState([]);
  const [conditions, setConditions] = useState(initialParams?.conditions || 'all');  // 'all' | 'dry' | 'wet' weather filter
  const [agg, setAgg] = useState(initialParams?.agg || 'best');               // 'median' | 'best' — style/grip lap basis
  const [cornerAgg, setCornerAgg] = useState(initialParams?.cornerAgg || 'best');   // corner chart basis: 'median'|'best'|'bestpass'
  const [layout, setLayout] = useState(initialParams?.layout || 'tile');         // 'stack' | 'tile' chart layout
  const [avail, setAvail] = useState({ wet: true, dry: true });  // which filters have data
  const [tempBounds, setTempBounds] = useState(null);   // {min,max} in display unit, or null
  const [tempRange, setTempRange] = useState(null);     // [lo,hi] live slider value (display unit)
  const [tempApplied, setTempApplied] = useState(null); // [lo,hi] committed on release → drives fetch
  const [timeBounds, setTimeBounds] = useState(null);   // {min,max} in ms (session date span), or null
  const [timeRange, setTimeRange] = useState(null);     // [lo,hi] ms — applied client-side, live
  const [cornerList, setCornerList] = useState([]);     // [{corner_id,label}] for the corner selector
  const [cornerSel, setCornerSel] = useState(initialParams?.cornerSel ?? null);     // selected corner_id
  const [cornerProg, setCornerProg] = useState([]);     // per-session trend for the selected corner
  const [gaps, setGaps] = useState([]);
  const [summary, setSummary] = useState(null);
  const [benchmark, setBenchmark] = useState(null);
  const [craft, setCraft] = useState(null);   // cross-car driver-craft comparison
  const [techByCorner, setTechByCorner] = useState({}); // corner_id -> tech | 'loading'
  const [loadingTrack, setLoadingTrack] = useState(false);
  // Restored temp/time range, applied once by the bounds effect below (which
  // otherwise always resets both to the freshly-fetched full span).
  const restoreTempAppliedRef = useRef(initialParams?.tempApplied || null);
  const restoreTimeRangeRef = useRef(initialParams?.timeRange || null);

  // Career rollup — reloads when the viewed driver changes (admin impersonation).
  // Also the source of truth for the track selector: only tracks the driver has
  // their own data for (career_overview is own-entries only).
  useEffect(() => {
    let cancelled = false;
    setOverview(null);
    getCareerOverview(viewUserId, careerVehicleId)
      .then(ov => !cancelled && setOverview(ov))
      .catch(e => !cancelled && setError(String(e.message || e)));
    return () => { cancelled = true; };
  }, [viewUserId, careerVehicleId]);

  // Global car list for the career selector. Reset the scope when the viewed
  // driver changes (a car id is meaningless across drivers) — except the very
  // first run, which may be restoring a careerVehicleId from initialParams.
  const firstCareerVehicleEffectRef = useRef(true);
  useEffect(() => {
    let cancelled = false;
    if (firstCareerVehicleEffectRef.current) firstCareerVehicleEffectRef.current = false;
    else setCareerVehicleId(null);
    getUserVehicles(viewUserId)
      .then(vs => !cancelled && setAllVehicles(vs))
      .catch(() => !cancelled && setAllVehicles([]));
    return () => { cancelled = true; };
  }, [viewUserId]);

  // Load the viewed driver's cars at the selected track, default to most-driven.
  useEffect(() => {
    if (!trackId) { setVehicles([]); return; }
    let cancelled = false;
    getUserTrackVehicles(trackId, viewUserId).then(vs => {
      if (cancelled) return;
      setVehicles(vs);
      setVehicleId(prev => (vs.some(v => v.id === prev) ? prev : (vs[0]?.id || null)));
    }).catch(() => !cancelled && setVehicles([]));
    return () => { cancelled = true; };
  }, [trackId, viewUserId]);

  // Load the condition-independent track data (corner gaps, field summary,
  // benchmark, craft). Kept separate from progression so toggling the weather
  // filter doesn't needlessly re-fetch these.
  useEffect(() => {
    if (tab !== 'track' || !trackId || !vehicleId) return;
    let cancelled = false;
    setLoadingTrack(true);
    setTechByCorner({});
    Promise.all([
      getTrackCornerGaps(trackId, vehicleId, viewUserId),
      getTrackFieldSummary(trackId, vehicleId, viewUserId),
      getBenchmarkLap(trackId, vehicleId, viewUserId),
      getTrackCraft(trackId, viewUserId),
    ]).then(([g, s, bm, cr]) => {
      if (cancelled) return;
      setGaps(g); setSummary(s); setBenchmark(bm); setCraft(cr);
    }).catch(e => !cancelled && setError(String(e.message || e)))
      .finally(() => !cancelled && setLoadingTrack(false));
    return () => { cancelled = true; };
  }, [tab, trackId, vehicleId, viewUserId]);

  // Display temperature unit + conversions (data is stored in °C).
  const tempUnit = unitPrefs?.temp === 'C' ? 'C' : 'F';
  const cToDisp = useCallback((c) => tempUnit === 'F' ? c * 9 / 5 + 32 : c, [tempUnit]);
  const dispToC = useCallback((v) => tempUnit === 'F' ? (v - 32) * 5 / 9 : v, [tempUnit]);

  // Filter bounds for this (track, vehicle): which of wet/dry have data (to grey
  // out unusable buttons), the air-temperature range, and the date span — all
  // from the *unfiltered* set so they're stable as filters change.
  useEffect(() => {
    if (tab !== 'track' || !trackId || !vehicleId) {
      setAvail({ wet: true, dry: true }); setTempBounds(null); setTimeBounds(null); return;
    }
    let cancelled = false;
    getTrackProgression(trackId, vehicleId, viewUserId, 'all').then(all => {
      if (cancelled) return;
      const wet = all.some(p => p.is_wet === true);
      const dry = all.some(p => p.is_wet !== true);   // dry filter = is_wet IS NOT TRUE (incl. unknown)
      setAvail({ wet, dry });
      setConditions(c => (c === 'wet' && !wet) || (c === 'dry' && !dry) ? 'all' : c);

      // Temperature bounds (display unit, whole degrees spanning the data).
      // A restored range (Back-navigation/deep-link) wins over the full span,
      // once, clamped to the freshly-fetched bounds in case the data shifted.
      const restoreTemp = restoreTempAppliedRef.current;
      restoreTempAppliedRef.current = null;
      const temps = all.map(p => p.weather?.temperature_c).filter(t => t != null);
      if (temps.length) {
        const lo = Math.floor(cToDisp(Math.min(...temps)));
        const hi = Math.ceil(cToDisp(Math.max(...temps)));
        const b = lo < hi ? { min: lo, max: hi } : null;   // need a real range to slide
        setTempBounds(b);
        const applied = (restoreTemp && b)
          ? [Math.max(b.min, restoreTemp[0]), Math.min(b.max, restoreTemp[1])]
          : (b ? [b.min, b.max] : null);
        setTempRange(applied);
        setTempApplied(applied);
      } else { setTempBounds(null); setTempRange(null); setTempApplied(null); }

      // Time bounds (ms) from the session dates. Same restore-once treatment.
      const restoreTime = restoreTimeRangeRef.current;
      restoreTimeRangeRef.current = null;
      const ts = all.map(p => p.event_date && new Date(p.event_date).getTime())
                    .filter(t => t && !Number.isNaN(t));
      if (ts.length) {
        const lo = Math.min(...ts), hi = Math.max(...ts);
        const b = lo < hi ? { min: lo, max: hi } : null;
        setTimeBounds(b);
        setTimeRange((restoreTime && b)
          ? [Math.max(b.min, restoreTime[0]), Math.min(b.max, restoreTime[1])]
          : (b ? [b.min, b.max] : null));
      } else { setTimeBounds(null); setTimeRange(null); }
    }).catch(() => { if (!cancelled) { setAvail({ wet: true, dry: true }); setTempBounds(null); setTimeBounds(null); } });
    return () => { cancelled = true; };
  }, [tab, trackId, vehicleId, viewUserId, cToDisp]);

  // Server-side temperature params (°C). Only constrain a side when its handle is
  // off the bound, so the exact min/max sessions aren't lost to float rounding.
  const tMinC = (tempBounds && tempApplied && tempApplied[0] > tempBounds.min) ? dispToC(tempApplied[0]) : null;
  const tMaxC = (tempBounds && tempApplied && tempApplied[1] < tempBounds.max) ? dispToC(tempApplied[1]) : null;

  // Progression chart — re-fetches on the weather + temperature filters (the
  // time filter is applied client-side below; graph-only scope).
  useEffect(() => {
    if (tab !== 'track' || !trackId || !vehicleId) return;
    let cancelled = false;
    getTrackProgression(trackId, vehicleId, viewUserId, conditions, tMinC, tMaxC, agg)
      .then(prog => !cancelled && setProgression(prog))
      .catch(e => !cancelled && setError(String(e.message || e)));
    return () => { cancelled = true; };
  }, [tab, trackId, vehicleId, viewUserId, conditions, tMinC, tMaxC, agg]);

  // Style-percentile trend (corner-vector store). Only track/car/driver scope
  // it — weather/temp/lap-basis filters have no corner-vector analog; the
  // Period slider applies client-side like the other charts. Best-effort:
  // tracks without vector coverage just render no cards.
  const [styleTrend, setStyleTrend] = useState(null);
  useEffect(() => {
    if (tab !== 'track' || !trackId || !vehicleId) { setStyleTrend(null); return; }
    let cancelled = false;
    getStyleTrend(trackId, vehicleId, viewUserId)
      .then(d => !cancelled && setStyleTrend(d))
      .catch(() => !cancelled && setStyleTrend(null));
    return () => { cancelled = true; };
  }, [tab, trackId, vehicleId, viewUserId]);

  // Corner list (corners the user has actually driven with this car) for the
  // per-corner progression selector.
  useEffect(() => {
    if (tab !== 'track' || !trackId || !vehicleId) { setCornerList([]); setCornerSel(null); return; }
    let cancelled = false;
    getTrackCorners(trackId, vehicleId, viewUserId).then(cs => {
      if (cancelled) return;
      setCornerList(cs);
      setCornerSel(prev => (cs.some(c => c.corner_id === prev) ? prev : (cs[0]?.corner_id || null)));
    }).catch(() => !cancelled && setCornerList([]));
    return () => { cancelled = true; };
  }, [tab, trackId, vehicleId, viewUserId]);

  // Per-corner trend — re-fetches on corner selection + the weather/temperature
  // filters (time filter is applied client-side, like the main chart).
  useEffect(() => {
    if (tab !== 'track' || !trackId || !vehicleId || !cornerSel) { setCornerProg([]); return; }
    let cancelled = false;
    getTrackCornerProgression(trackId, cornerSel, vehicleId, viewUserId, conditions, tMinC, tMaxC, cornerAgg)
      .then(d => !cancelled && setCornerProg(d))
      .catch(() => !cancelled && setCornerProg([]));
    return () => { cancelled = true; };
  }, [tab, trackId, vehicleId, cornerSel, viewUserId, conditions, tMinC, tMaxC, cornerAgg]);

  // The corner chart follows the global Typical/Best toggle by default, but has
  // its own basis control (incl. "Best pass"); changing the global one re-syncs
  // it — except on mount, where a restored cornerAgg may deliberately differ.
  const firstCornerAggSyncRef = useRef(true);
  useEffect(() => {
    if (firstCornerAggSyncRef.current) { firstCornerAggSyncRef.current = false; return; }
    setCornerAgg(agg);
  }, [agg]);

  // Mirror the full filter/selection state up to App.jsx (→ URL) so a
  // Back-navigation or deep-link can restore this page exactly as left.
  useEffect(() => {
    onParamsChange?.({
      tab, trackId, vehicleId, careerVehicleId,
      conditions, agg, cornerAgg, layout,
      tempApplied, timeRange, cornerSel,
      adminMode, viewUser,
    });
  }, [tab, trackId, vehicleId, careerVehicleId, conditions, agg, cornerAgg, layout,
      tempApplied, timeRange, cornerSel, adminMode, viewUser, onParamsChange]);

  const openTrack = useCallback((tid) => {
    // On a track change, seed the car with whatever scopes the career view
    // (null = let the per-track effect default to most-driven). The vehicles
    // effect keeps it only if that car was actually driven here, otherwise it
    // falls back — so this is a preference, not a constraint. On a no-op
    // re-select we leave vehicleId alone (the effect won't re-run to re-default).
    if (tid !== trackId) setVehicleId(careerVehicleId);
    setTrackId(tid);
    setTab('track');
  }, [trackId, careerVehicleId]);

  const loadTechnique = useCallback((cornerId) => {
    setTechByCorner(prev => {
      if (prev[cornerId]) return prev;  // already loaded/loading
      getTrackCornerTechnique(trackId, cornerId, vehicleId, viewUserId)
        .then(t => setTechByCorner(p => ({ ...p, [cornerId]: t })))
        .catch(() => setTechByCorner(p => ({ ...p, [cornerId]: null })));
      return { ...prev, [cornerId]: 'loading' };
    });
  }, [trackId, vehicleId, viewUserId]);

  // The time-window predicate (client-side — just a date comparison). At full
  // span it's a no-op and keeps undated sessions; once narrowed, undated sessions
  // can't be placed so they drop out. Shared by the main chart and corner trend.
  const withinTimeWindow = useCallback((eventDate) => {
    if (!timeRange || !timeBounds) return true;
    const [lo, hi] = timeRange;
    if (lo <= timeBounds.min && hi >= timeBounds.max) return true;
    if (!eventDate) return false;
    const t = new Date(eventDate).getTime();
    return t >= lo && t <= hi;
  }, [timeRange, timeBounds]);

  const visible = useMemo(
    () => progression.filter(p => withinTimeWindow(p.event_date)),
    [progression, withinTimeWindow]);
  const visibleCorner = useMemo(
    () => cornerProg.filter(p => withinTimeWindow(p.event_date)),
    [cornerProg, withinTimeWindow]);

  // Sessions/laps the track view is based on (vehicle-scoped, like everything
  // else on this tab — one row per session for the selected car, after filters).
  const dataBasis = useMemo(() => {
    if (!visible.length) return null;
    const dates = visible
      .map(p => p.event_date)
      .filter(Boolean)
      .sort((a, b) => new Date(a) - new Date(b));   // chronological by parsed time
    const from = dates[0] || null;
    const to = dates[dates.length - 1] || null;
    const spanDays = (from && to && from !== to)
      ? Math.round((new Date(to) - new Date(from)) / 86400000)
      : null;
    return {
      sessions: visible.length,
      laps: visible.reduce((a, p) => a + (p.n_laps || 0), 0),
      from, to, spanDays,
    };
  }, [visible]);

  // Progression chart data + the personal-best point to highlight (fastest lap
  // = smallest best_lap_seconds). Built once so the marker and the line agree.
  const chart = useMemo(() => {
    const data = visible.map((p, i) => {
      // Coasting is a % of one lap — convert to seconds against the lap that
      // sourced it: the best lap under "Best", the typical (median) lap under
      // "Typical". Mirrors the leaderboard's "x s coasting" hover.
      const lapBasis = agg === 'best' ? p.best_lap_seconds : p.median_lap_seconds;
      return {
        i, date: p.event_date || `#${i + 1}`,
        session_id: p.session_id,
        best: p.best_lap_seconds,
        median: p.median_lap_seconds,
        score: p.median_driver_score != null ? p.median_driver_score * 100 : null,
        is_wet: p.is_wet,
        weather: p.weather,
        // Style + grip + consistency trends (see track_progression aggregates).
        traction: p.traction, smoothness: p.smoothness, coasting: p.coasting,
        coast_s: (p.coasting != null && lapBasis != null) ? (p.coasting / 100) * lapBasis : null,
        pct_braking: p.pct_braking, pct_accelerating: p.pct_accelerating, oversteer: p.oversteer,
        pcg: p.peak_combined_g, pbg: p.peak_braking_g, plg: p.peak_lat_g,
        sd: p.lap_time_sd,
        top_mph: p.top_speed_mph, avg_mph: p.avg_speed_mph,
      };
    });
    const pb = data.reduce(
      (best, d) => (d.best != null && (best == null || d.best < best.best) ? d : best),
      null);
    return { data, pb };
  }, [visible, agg]);

  // Style-trend chart data (time-window applied; axis keys spread as columns).
  const styleTrendData = useMemo(() => {
    if (!styleTrend?.points?.length) return [];
    return styleTrend.points
      .filter(p => withinTimeWindow(p.date))
      .map((p, i) => ({
        i, date: p.date || '', session_id: p.session_id, n_passes: p.n_passes,
        ...p.values,
      }));
  }, [styleTrend, withinTimeWindow]);

  // Session → best lap time, for the radar caption. Keyed off the full
  // progression (not `visible`): the radar ignores the weather/temp filters,
  // so its frames can cover sessions the filtered progression drops.
  const lapBySession = useMemo(() => {
    const m = {};
    for (const p of progression) {
      if (p.session_id && p.best_lap_seconds != null) m[p.session_id] = p.best_lap_seconds;
    }
    return m;
  }, [progression]);

  // Per-corner trend chart data (time-window applied).
  const cornerData = useMemo(() => visibleCorner.map((p, i) => ({
    i, date: p.event_date || '', session_id: p.session_id,
    min_mph: p.min_mph, brake_m: p.brake_m, throttle_m: p.throttle_m,
  })), [visibleCorner]);
  const cornerLabel = cornerList.find(c => c.corner_id === cornerSel)?.label || cornerSel;

  // Click a point on the progression chart → open that session in the analysis
  // view. Recharts v3 dropped activePayload from the event state, so we resolve
  // the clicked datum by activeTooltipIndex into the chart's own data array (each
  // row carries session_id). Callers pass their data array alongside the event.
  const handlePointClick = useCallback((e, data) => {
    const idx = e?.activeTooltipIndex ?? e?.activeIndex;
    const row = (idx != null && data) ? data[Number(idx)] : null;
    const sid = row?.session_id;
    if (sid && onLoadSession) onLoadSession(sid);
  }, [onLoadSession]);

  // Custom dot for the best-lap line: the personal-best session gets a prominent,
  // labelled, clickable gold marker so it's easy to spot among the dense points.
  const renderBestDot = useCallback((props) => {
    const { cx, cy, payload, index } = props;
    if (cx == null || cy == null) return <g key={index} />;
    const isPb = chart.pb && payload?.session_id === chart.pb.session_id;
    if (!isPb) {
      return <circle key={index} cx={cx} cy={cy} r={2} fill="#60a5fa" fillOpacity={0.7} />;
    }
    // The PB is the fastest lap, so on the reversed axis it sits near the top —
    // a label above the dot (cy - 11) clips against the chart's top edge. Drop it
    // below the dot whenever there isn't room above.
    const below = cy < 24;
    return (
      <g key={index} style={{ cursor: 'pointer' }}
         onClick={() => payload?.session_id && onLoadSession?.(payload.session_id)}>
        <circle cx={cx} cy={cy} r={6} fill="#fbbf24" stroke={chartColors.bg} strokeWidth={2} />
        <text x={cx} y={below ? cy + 19 : cy - 11} textAnchor="middle"
              fill="#fbbf24" fontSize={11} fontWeight={600}>
          PB {fmtLap(payload.best)}
        </text>
      </g>
    );
  }, [chart.pb, onLoadSession, chartColors.bg]);

  // Time-window quick presets, relative to this track's date span.
  const YEAR_MS = 365 * 86400000;
  const applyTimePreset = useCallback((key) => {
    if (!timeBounds) return;
    const { min, max } = timeBounds;
    if (key === 'first') setTimeRange([min, Math.min(max, min + YEAR_MS)]);
    else if (key === 'last') setTimeRange([Math.max(min, max - YEAR_MS), max]);
    else if (key === 'thisyear') {
      const soy = new Date(new Date().getFullYear(), 0, 1).getTime();
      setTimeRange([Math.min(Math.max(min, soy), max), max]);
    }
    else setTimeRange([min, max]);   // 'all'
  }, [timeBounds, YEAR_MS]);
  const tempNarrowed = tMinC != null || tMaxC != null;
  const timeNarrowed = !!(timeBounds && timeRange &&
    (timeRange[0] > timeBounds.min || timeRange[1] < timeBounds.max));

  const loadGhost = useCallback(() => {
    if (!benchmark || !onCompareSessions) return;
    // Newest own session at this track = last progression point.
    const mine = progression.length ? progression[progression.length - 1].session_id : null;
    if (mine) onCompareSessions(mine, [benchmark.session_id]);
    else onCompareSessions(benchmark.session_id);  // no own session — load ghost directly
  }, [benchmark, progression, onCompareSessions]);

  const pickDriver = useCallback((u) => {
    setViewUser({ id: u.id, name: u.display_name || u.email });
    setDriverQuery(''); setDriverResults([]);
    setTab('career');     // land on the impersonated driver's career rollup
    setTrackId(null); setVehicleId(null);
  }, []);

  const clearDriver = useCallback(() => {
    setViewUser(null);
    setTab('career'); setTrackId(null); setVehicleId(null);
  }, []);

  // Back is two-level: from a track's detail view it returns to the career
  // rollup (clearing the track, which also drops it from the URL via the
  // onParamsChange effect); from the career view it leaves Progress entirely.
  const handleBack = useCallback(() => {
    if (tab === 'track') {
      setTab('career'); setTrackId(null); setVehicleId(null);
      return;
    }
    onBack?.();
  }, [tab, onBack]);

  // ── Career headline tiles ────────────────────────────────────────────────
  const career = useMemo(() => {
    if (!overview) return null;
    const totalSessions = overview.reduce((a, o) => a + o.n_sessions, 0);
    const totalLaps = overview.reduce((a, o) => a + o.n_laps, 0);
    const improved = overview
      .filter(o => o.improvement_s != null)
      .sort((a, b) => b.improvement_s - a.improvement_s)[0] || null;
    return { totalSessions, totalLaps, nTracks: overview.length, improved };
  }, [overview]);

  if (error) {
    return (
      <div className={styles.page}>
        <header className={styles.header}>
          <button className={styles.back} onClick={handleBack}>← Back</button>
          <h1>Progress</h1>
        </header>
        <div className={styles.error}>Couldn’t load analytics: {error}</div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <button className={styles.back} onClick={handleBack}>← Back</button>
        <h1>Progress</h1>
        {viewUser && (
          <span className={styles.viewingChip}>
            Viewing: <strong>{viewUser.name}</strong>
            <button className={styles.chipX} onClick={clearDriver} title="Back to your own progress">✕</button>
          </span>
        )}
        {isAdmin && (
          <div className={styles.adminBox}>
            <button
              className={adminMode ? styles.adminOn : styles.adminOff}
              onClick={() => { setAdminMode(a => !a); setDriverQuery(''); setDriverResults([]); }}
              title="View another driver's progress (admin)">
              ⚙ Admin{adminMode ? '' : ' (off)'}
            </button>
            {adminMode && (
              <div className={styles.driverSearch}>
                <input
                  type="text" value={driverQuery} placeholder="Search driver…"
                  onChange={e => setDriverQuery(e.target.value)} />
                {driverResults.length > 0 && (
                  <div className={styles.driverResults}>
                    {driverResults.map(u => (
                      <div key={u.id} className={styles.driverResult} onClick={() => pickDriver(u)}>
                        {u.display_name || u.email}
                        {u.display_name && <span className={styles.muted}> · {u.email}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <div className={styles.tabs}>
          <button className={tab === 'career' ? styles.tabActive : styles.tab}
                  onClick={() => setTab('career')}>Career</button>
          <button className={tab === 'track' ? styles.tabActive : styles.tab}
                  onClick={() => setTab('track')} disabled={!trackId && !overview?.length}>
            Track
          </button>
        </div>
      </header>

      {tab === 'career' && (
        <section className={styles.body}>
          {allVehicles.length > 0 && (
            <div className={styles.selectors}>
              <select value={careerVehicleId || ''}
                      onChange={e => setCareerVehicleId(e.target.value || null)}
                      title="Scope your career stats to one car">
                <option value="">All cars</option>
                {allVehicles.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
          )}
          {!overview ? <div className={styles.muted}>Loading…</div> : (
            <>
              <div className={styles.tiles}>
                <Tile label="Tracks" value={career.nTracks} />
                <Tile label="Sessions" value={career.totalSessions} />
                <Tile label="Laps" value={career.totalLaps} />
                <Tile label="Most improved"
                      value={career.improved
                        ? `${career.improved.track_name} (−${career.improved.improvement_s.toFixed(2)}s)`
                        : '—'} />
              </div>

              {overview.length === 0 ? (
                <div className={styles.muted}>No leaderboard history yet. Upload a session to get started.</div>
              ) : (
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Track</th><th>Sessions</th><th>Laps</th>
                      <th>Best lap</th><th>Best score</th><th>Improvement</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {overview.map(o => (
                      <tr key={o.track_id} className={styles.row} onClick={() => openTrack(o.track_id)}>
                        <td className={styles.trackName}>{o.track_name}</td>
                        <td>{o.n_sessions}</td>
                        <td>{o.n_laps}</td>
                        <td>{fmtLap(o.best_lap_seconds)}</td>
                        <td>{o.best_driver_score != null ? Math.round(o.best_driver_score * 100) : '—'}</td>
                        <td className={o.improvement_s > 0 ? styles.gapGood : o.improvement_s < 0 ? styles.gapBad : ''}>
                          {o.improvement_s != null
                            ? `${o.improvement_s > 0 ? '−' : '+'}${Math.abs(o.improvement_s).toFixed(2)}s`
                            : '—'}
                        </td>
                        <td className={styles.chev}>›</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </section>
      )}

      {tab === 'track' && (
        <section className={styles.body}>
          <div className={styles.selectors}>
            <select value={trackId || ''} onChange={e => openTrack(e.target.value)}>
              <option value="" disabled>Select a track…</option>
              {(overview || []).map(o => <option key={o.track_id} value={o.track_id}>{o.track_name}</option>)}
            </select>
            {vehicles.length > 0 && (
              <select value={vehicleId || ''} onChange={e => setVehicleId(e.target.value)}>
                {vehicles.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            )}
            {trackId && (
              <div className={styles.seg} role="group" aria-label="Weather filter">
                {[['all', 'All'], ['dry', 'Dry'], ['wet', 'Wet']].map(([val, label]) => {
                  const disabled = (val === 'wet' && !avail.wet) || (val === 'dry' && !avail.dry);
                  return (
                    <button key={val}
                            className={conditions === val ? styles.segOn : styles.segBtn}
                            disabled={disabled}
                            onClick={() => setConditions(val)}
                            title={disabled ? `No ${val} sessions here`
                                 : val === 'dry' ? 'Hide sessions run in the rain'
                                 : val === 'wet' ? 'Show only sessions run in the rain'
                                 : 'Show all sessions'}>
                      {label}
                    </button>
                  );
                })}
              </div>
            )}
            {trackId && (
              <div className={styles.seg} role="group" aria-label="Lap basis">
                {[['median', 'Typical'], ['best', 'Best lap']].map(([val, label]) => (
                  <button key={val}
                          className={agg === val ? styles.segOn : styles.segBtn}
                          onClick={() => setAgg(val)}
                          title={val === 'best' ? 'Style & grip metrics from each session’s fastest lap'
                                                 : 'Style & grip metrics = each session’s typical (median) lap'}>
                    {label}
                  </button>
                ))}
              </div>
            )}
            {trackId && (
              <div className={styles.seg} role="group" aria-label="Chart layout">
                {[['stack', 'Stacked'], ['tile', 'Tiled']].map(([val, label]) => (
                  <button key={val}
                          className={layout === val ? styles.segOn : styles.segBtn}
                          onClick={() => setLayout(val)}
                          title={val === 'tile' ? 'Two-column grid of charts' : 'One chart per row'}>
                    {label}
                  </button>
                ))}
              </div>
            )}
            {dataBasis && (
              <span className={styles.dataBasis}>
                Based on <strong>{dataBasis.sessions}</strong> session{dataBasis.sessions === 1 ? '' : 's'}
                {' · '}<strong>{dataBasis.laps}</strong> lap{dataBasis.laps === 1 ? '' : 's'}
                {dataBasis.from && (
                  dataBasis.from === dataBasis.to
                    ? <> on {fmtDate(dataBasis.from)}</>
                    : <> from {fmtDate(dataBasis.from)} to {fmtDate(dataBasis.to)}
                        {dataBasis.spanDays != null && <> [{dataBasis.spanDays} days]</>}</>
                )}
              </span>
            )}
          </div>

          {trackId && (tempBounds || timeBounds) && (
            <div className={styles.filterBar}>
              {tempBounds && tempRange && (
                <div className={styles.filterGroup}>
                  <div className={styles.filterHead}>
                    <span className={styles.filterLabel}>Temp</span>
                    {tempNarrowed && (
                      <button className={styles.filterReset}
                              onClick={() => { setTempRange([tempBounds.min, tempBounds.max]);
                                               setTempApplied([tempBounds.min, tempBounds.max]); }}>
                        reset
                      </button>
                    )}
                  </div>
                  <RangeSlider min={tempBounds.min} max={tempBounds.max} step={1}
                               value={tempRange} onChange={setTempRange} onCommit={setTempApplied}
                               format={v => `${Math.round(v)}°${tempUnit}`} />
                </div>
              )}
              {timeBounds && timeRange && (
                <div className={styles.filterGroup}>
                  <div className={styles.filterHead}>
                    <span className={styles.filterLabel}>Period</span>
                    <div className={styles.timePresets}>
                      {[['all', 'All'], ['thisyear', 'This yr'], ['last', 'Last yr'], ['first', 'First yr']].map(([key, label]) => (
                        <button key={key} className={styles.filterReset} onClick={() => applyTimePreset(key)}>
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <RangeSlider min={timeBounds.min} max={timeBounds.max} step={86400000}
                               value={timeRange} onChange={setTimeRange}
                               format={ms => fmtDate(ms)} />
                </div>
              )}
            </div>
          )}

          {!trackId ? (
            <div className={styles.muted}>Pick a track to see your progression and where you stand vs the field.</div>
          ) : loadingTrack ? (
            <div className={styles.muted}>Loading…</div>
          ) : (
            <>
              {/* Time-loss headline + composed target */}
              {summary && summary.own_best_lap_s != null && (
                <div className={styles.headline}>
                  {summary.total_est_loss_s > 0.05 ? (
                    <>
                      <strong>~{summary.total_est_loss_s.toFixed(1)}s</strong> off field pace
                      {summary.corners.length > 0 && (
                        <> — biggest in {summary.corners.slice(0, 2).map(c => c.label).join(' & ')}</>
                      )}.
                      <span className={styles.target}>
                        Realistic target: <strong>{fmtLap(summary.realistic_target_s)}</strong>
                        {' '}(your best {fmtLap(summary.own_best_lap_s)})
                      </span>
                    </>
                  ) : (
                    <>You’re at or above field pace through the corners here — strong baseline.</>
                  )}
                </div>
              )}

              {/* Benchmark ghost — self-view only (loading telemetry is a
                  different permission domain than read-only analytics). */}
              {benchmark && !viewUser && (
                <div className={styles.ghost}>
                  Fastest comparable lap available: <strong>{benchmark.lap_time_str}</strong>
                  {' '}by {benchmark.driver_name || 'another driver'}.
                  <button className={styles.ghostBtn} onClick={loadGhost}>Load benchmark lap →</button>
                </div>
              )}

              {/* Progression chart */}
              {progression.length > 0 && visible.length === 0 && (
                <div className={styles.muted}>No sessions match the current filters.</div>
              )}
              <div className={layout === 'tile' ? styles.chartGrid : styles.chartStack}>
              {visible.length > 0 && (
                <div className={styles.chartWrap}>
                  <div className={styles.chartTitle}>
                    Best lap & driver score over time
                    <InfoDot text={TIPS.bestScore} />
                    {chart.pb && (
                      <span className={styles.chartPb}>★ Best lap {fmtLap(chart.pb.best)}</span>
                    )}
                    <span className={styles.chartHint}>· click a point to open that session</span>
                  </div>
                  <div ref={mainTooltip.containerRef} style={{ width: '100%', height: 280 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chart.data} onClick={(e) => handlePointClick(e, chart.data)}
                               accessibilityLayer={false}
                               onMouseMove={mainTooltip.onMouseMove}
                               margin={{ top: 16, right: 8, left: 0, bottom: 0 }}
                               style={{ cursor: 'pointer' }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                      {/* Use the unique row index as the category key — event_date
                          repeats (several sessions per day) and duplicate band-axis
                          categories break Recharts' hover→active-point mapping
                          (active dots snap to the wrong point). Format back to date. */}
                      <XAxis dataKey="i" type="category"
                             tickFormatter={(i) => chart.data[i]?.date ?? ''}
                             tick={{ fill: chartColors.tick, fontSize: 11 }} />
                      <YAxis yAxisId="lap" reversed tick={{ fill: chartColors.tick, fontSize: 11 }}
                             tickFormatter={fmtLap} width={60} domain={['auto', 'auto']} />
                      <YAxis yAxisId="score" orientation="right" domain={[0, 100]}
                             tick={{ fill: chartColors.tick, fontSize: 11 }} width={30} />
                      <Tooltip content={<ProgressTooltip unitPrefs={unitPrefs} />}
                               position={mainTooltip.position}
                               wrapperStyle={mainTooltip.wrapperStyle} />
                      {/* Personal-best highlight: dashed line at the PB time + a
                          gold marker on the session that set it. */}
                      {chart.pb && (
                        <ReferenceLine yAxisId="lap" y={chart.pb.best}
                                       stroke="#fbbf24" strokeDasharray="4 4" strokeOpacity={0.6} />
                      )}
                      {/* Typical (median clean) lap — the gap to Best is a consistency read */}
                      <Line yAxisId="lap" type="monotone" dataKey="median" stroke="#94a3b8"
                            strokeWidth={1.5} strokeDasharray="5 4" dot={false} connectNulls />
                      <Line yAxisId="lap" type="monotone" dataKey="best" stroke="#60a5fa" strokeWidth={2}
                            dot={renderBestDot} activeDot={{ r: 5 }} />
                      <Line yAxisId="score" type="monotone" dataKey="score" stroke="#34d399" strokeWidth={2} dot />
                    </LineChart>
                  </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Playable style radar — the 8-axis polygon over time
                  (corner-vector store; per-session ranks vs the all-time field) */}
              <StyleRadarCard styleTrend={styleTrend} data={styleTrendData}
                              lapBySession={lapBySession} anchoredTooltip={pinTooltips} />

              {/* Speed trends — top + average lap speed */}
              <TrendChart
                title="Speed over time"
                hint="· top & average lap speed"
                tip={TIPS.speed}
                data={chart.data}
                anchoredTooltip={pinTooltips}
                onPointClick={handlePointClick}
                valueFormat={v => `${Math.round(v)} mph`}
                yTickFormat={v => Math.round(v)}
                yUnit="mph"
                lines={[
                  { key: 'top_mph', name: 'Top speed', color: '#60a5fa' },
                  { key: 'avg_mph', name: 'Avg speed', color: '#34d399' },
                ]} />

              {/* Driving-style trends — the craft metrics over time */}
              <TrendChart
                title="Driving style over time"
                hint="· grip usage & traction higher is better; smoothness lower is better"
                tip={TIPS.style}
                data={chart.data}
                anchoredTooltip={pinTooltips}
                onPointClick={handlePointClick}
                yDomain={[0, 100]}
                valueFormat={v => `${Math.round(v)}`}
                lines={[
                  { key: 'score',     name: 'Grip usage', color: '#34d399' },
                  { key: 'traction',  name: 'Traction',   color: '#60a5fa' },
                  { key: 'smoothness',name: 'Smoothness', color: '#a78bfa' },
                ]} />

              {/* Coasting — its own chart (scale differs from the style metrics) */}
              <TrendChart
                title="Time coasting over time"
                hint="· % of lap off-throttle & unloaded — lower is better"
                tip={TIPS.coasting}
                data={chart.data}
                anchoredTooltip={pinTooltips}
                onPointClick={handlePointClick}
                valueFormat={v => `${v.toFixed(1)}%`}
                detail={(key, d) => key === 'coasting' && d?.coast_s != null ? `${d.coast_s.toFixed(2)} s` : null}
                lines={[{ key: 'coasting', name: 'Coasting', color: '#f59e0b' }]} />

              {/* Throttle/brake balance — % of lap braking vs accelerating */}
              <TrendChart
                title="Throttle & brake balance over time"
                hint="· % of lap spent braking vs accelerating"
                data={chart.data}
                anchoredTooltip={pinTooltips}
                onPointClick={handlePointClick}
                valueFormat={v => `${v.toFixed(1)}%`}
                lines={[
                  { key: 'pct_braking',      name: 'Braking',      color: '#f87171' },
                  { key: 'pct_accelerating', name: 'Accelerating', color: '#34d399' },
                ]} />

              {/* Oversteer — seconds per lap above the excess-yaw threshold */}
              <TrendChart
                title="Oversteer over time"
                hint="· seconds per lap of excess yaw — lower is more stable"
                data={chart.data}
                anchoredTooltip={pinTooltips}
                onPointClick={handlePointClick}
                valueFormat={v => `${v.toFixed(2)}s`}
                lines={[{ key: 'oversteer', name: 'Oversteer', color: '#fbbf24' }]} />

              {/* Grip used / aggression — peak G per session */}
              <TrendChart
                title="Grip used over time"
                hint="· peak G per session"
                tip={TIPS.grip}
                data={chart.data}
                anchoredTooltip={pinTooltips}
                onPointClick={handlePointClick}
                valueFormat={v => `${v.toFixed(2)}G`}
                lines={[
                  { key: 'pcg', name: 'Combined G', color: '#f87171' },
                  { key: 'pbg', name: 'Braking G',  color: '#60a5fa' },
                  { key: 'plg', name: 'Lateral G',  color: '#34d399' },
                ]} />

              {/* Consistency — lap-time spread per session (lower = tighter) */}
              <TrendChart
                title="Consistency over time"
                hint="· lap-time spread (σ) — lower is tighter"
                tip={TIPS.consistency}
                data={chart.data}
                anchoredTooltip={pinTooltips}
                onPointClick={handlePointClick}
                valueFormat={v => `${v.toFixed(1)}s`}
                lines={[{ key: 'sd', name: 'Lap-time σ', color: '#fbbf24' }]} />

              {/* Per-corner progression — one chart per metric (distinct scales) */}
              {cornerList.length > 0 && (
                <TrendChart
                  title={`Corner min speed — ${cornerLabel}`}
                  tip={TIPS.cornerMin}
                  data={cornerData}
                  anchoredTooltip={pinTooltips}
                  onPointClick={handlePointClick}
                  valueFormat={v => `${Math.round(v)} mph`}
                  yTickFormat={v => Math.round(v)}
                  yUnit="mph"
                  header={
                    <>
                      <select className={styles.cornerSelect} value={cornerSel || ''}
                              onChange={e => setCornerSel(e.target.value)}
                              onClick={e => e.stopPropagation()}>
                        {cornerList.map(c => (
                          <option key={c.corner_id} value={c.corner_id}>{c.label}</option>
                        ))}
                      </select>
                      <span className={styles.seg} role="group" aria-label="Corner basis"
                            onClick={e => e.stopPropagation()} style={{ marginLeft: '0.5rem' }}>
                        {[['median', 'Typical'], ['best', 'Best lap'], ['bestpass', 'Best pass']].map(([val, label]) => (
                          <button key={val}
                                  className={cornerAgg === val ? styles.segOn : styles.segBtn}
                                  onClick={() => setCornerAgg(val)}
                                  title={val === 'bestpass' ? 'Outright fastest min-speed each session (any lap)'
                                       : val === 'best' ? 'Values from each session’s fastest lap'
                                       : 'Each session’s typical (median) lap'}>
                            {label}
                          </button>
                        ))}
                      </span>
                    </>
                  }
                  lines={[{ key: 'min_mph', name: 'Min speed (mph)', color: '#60a5fa' }]} />
              )}
              {cornerList.length > 0 && (
                <TrendChart
                  title={`Corner brake point — ${cornerLabel}`}
                  hint="· m before apex — bigger = brake earlier"
                  tip={TIPS.cornerBrake}
                  data={cornerData}
                  anchoredTooltip={pinTooltips}
                  onPointClick={handlePointClick}
                  valueFormat={v => `${Math.round(v)} m`}
                  lines={[{ key: 'brake_m', name: 'Brake point (m)', color: '#f87171' }]} />
              )}
              {cornerList.length > 0 && (
                <TrendChart
                  title={`Corner throttle pickup — ${cornerLabel}`}
                  hint="· m after apex — smaller = back to power sooner"
                  tip={TIPS.cornerThrottle}
                  data={cornerData}
                  anchoredTooltip={pinTooltips}
                  onPointClick={handlePointClick}
                  valueFormat={v => `${Math.round(v)} m`}
                  lines={[{ key: 'throttle_m', name: 'Throttle pickup (m)', color: '#34d399' }]} />
              )}
              </div>

              {/* Driver craft vs the field — car-agnostic, pools all cars */}
              {craft && craft.metrics.length > 0 && (
                <div className={styles.craftWrap}>
                  <div className={styles.chartTitle}>
                    Driver craft vs the field — all cars ({craft.n_drivers} drivers)
                  </div>
                  {!craft.enough && (
                    <div className={styles.muted} style={{ marginBottom: '0.4rem' }}>
                      Small field — treat the ranking as a rough band.
                    </div>
                  )}
                  {craft.metrics.map(m => (
                    <div key={m.key} className={styles.craftRow}>
                      <span className={styles.craftLabel}>{m.label}</span>
                      <div className={styles.craftBar}>
                        {m.percentile != null && (
                          <div className={styles.craftFill} style={{ width: `${m.percentile}%` }} />
                        )}
                      </div>
                      <span className={styles.craftVals}>
                        <strong>{m.own}</strong>
                        <span className={styles.muted}> · field {m.field_median ?? '—'}</span>
                        {m.rank != null && (
                          <span className={m.percentile >= 50 ? styles.gapGood : styles.gapWarn}
                                title={`${ordinal(m.percentile)} percentile`}>
                            {' '}· {ordinal(m.rank)} of {m.n_field} driver{m.n_field === 1 ? '' : 's'}
                          </span>
                        )}
                      </span>
                    </div>
                  ))}
                  <div className={styles.muted} style={{ fontSize: '0.72rem', marginTop: '0.4rem' }}>
                    Car-normalized — grip usage, traction, coasting & smoothness compare across different cars.
                  </div>
                </div>
              )}

              {/* Per-corner gap to field */}
              {gaps.length > 0 ? (
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Corner</th><th>Your best</th><th>Field (p80)</th><th>Gap</th><th>Field</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {gaps.map(c => {
                      const tech = techByCorner[c.corner_id];
                      return (
                        <Fragment key={c.corner_id}>
                          <tr className={styles.row} onClick={() => loadTechnique(c.corner_id)}>
                            <td className={styles.trackName}>{c.label}</td>
                            <td>{c.own_mph} mph</td>
                            <td>{c.ref_mph} mph</td>
                            <td className={gapClass(c.gap_mph)}>
                              {c.gap_mph > 0 ? `−${c.gap_mph}` : `+${Math.abs(c.gap_mph)}`} mph
                            </td>
                            <td className={styles.muted}>{c.n_drivers} drivers</td>
                            <td className={styles.chev}>{tech ? '▾' : '▸'}</td>
                          </tr>
                          {tech && tech !== 'loading' && (
                            <tr className={styles.techRow}>
                              <td colSpan={6}>
                                {tech.n_field_laps > 0 && (tech.field_brake_m != null || tech.field_throttle_m != null) ? (
                                  <div className={styles.techGrid}>
                                    <TechCell label="Brake point (m before apex)"
                                              own={tech.own_brake_m} field={tech.field_brake_m} higherBetter />
                                    <TechCell label="Throttle pickup (m after apex)"
                                              own={tech.own_throttle_m} field={tech.field_throttle_m} />
                                  </div>
                                ) : (
                                  <span className={styles.muted}>No technique data for the field here yet.</span>
                                )}
                              </td>
                            </tr>
                          )}
                          {tech === 'loading' && (
                            <tr className={styles.techRow}>
                              <td colSpan={6}><span className={styles.muted}>Loading…</span></td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className={styles.muted}>
                  No same-vehicle field reference here yet — we need more data from other drivers in the same car.
                </div>
              )}
            </>
          )}
        </section>
      )}
    </div>
  );
}

// A small "ⓘ" that reveals an explanation on hover (native title — same
// mechanism the leaderboard column headers use).
function InfoDot({ text }) {
  if (!text) return null;
  return <span className={styles.infoDot} title={text} aria-label={text}>ⓘ</span>;
}

// Short axis labels for the playable radar (keys = style_trend axis keys; the
// backend's full labels crowd the polar axis).
const RADAR_SHORT = {
  braking_aggression: 'Braking', trail_braking: 'Trail',
  brake_turn_overlap: 'Overlap', corner_commitment: 'vMin',
  throttle_discipline: 'Throttle', consistency: 'Consistency',
  smoothness: 'Smoothness', rotation: 'Rotation',
};
const RADAR_FORM_N   = 5;    // trailing sessions averaged in "Form" mode
const RADAR_FRAME_MS = 700;  // playback cadence; the tween below is shorter

// Hover tooltip for the playable radar — mirrors the Similarity Lab's radar
// tooltip: full axis name, what it measures (AXIS_INFO.short), then the frame
// + period-average percentiles as ordinals.
function RadarAxisTip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  const info = AXIS_INFO[d?.key];
  return (
    <div className={styles.tooltip}>
      <div className={styles.tooltipDate}>{d?.label || d?.axis}</div>
      {info && <div className={styles.radarTipDesc}>{info.short}</div>}
      {payload.map(p => (
        <div key={p.dataKey}>
          {p.name}: <strong>{p.value == null ? 'n/a' : ordinal(p.value)}</strong>
        </div>
      ))}
    </div>
  );
}

// The 8-axis technique radar played over the visible sessions: a scrubber +
// play button animate the polygon frame by frame (Recharts tweens between data
// updates). A dashed outline of the period-average shape stays behind it so
// movement reads as deviation-from-usual. Frames come from styleTrendData, so
// the Period slider bounds the playable range for free.
function StyleRadarCard({ styleTrend, data, lapBySession, anchoredTooltip = false }) {
  const { chartColors } = useTheme();
  const radarTooltip = useChartEdgeTooltip(anchoredTooltip, 280);
  const [mode, setMode] = useState('form');   // 'session' | 'form' | 'todate'
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [showBestLap, setShowBestLap] = useState(true);

  const axes = styleTrend?.axes || [];

  // Absolute best lap at this track, independent of the Period slider. The
  // radar values come from that session's raw frame (never a Form/To-date
  // average), so it remains a stable benchmark while the timeline plays.
  const bestLapFrame = useMemo(() => {
    let best = null;
    for (const point of styleTrend?.points || []) {
      const lapTime = lapBySession?.[point.session_id];
      if (lapTime == null || !point.values) continue;
      if (!best || lapTime < best.lapTime) {
        best = {
          date: point.date || '',
          session_id: point.session_id,
          lapTime,
          values: point.values,
        };
      }
    }
    return best;
  }, [styleTrend?.points, lapBySession]);

  // One frame per session; values transformed per mode (nulls skipped per-axis,
  // so a missing channel in one session doesn't hole the whole window).
  const frames = useMemo(() => {
    if (!data?.length) return [];
    return data.map((d, i) => {
      const lo = mode === 'session' ? i
               : mode === 'form'    ? Math.max(0, i - RADAR_FORM_N + 1)
               : 0;
      const win = data.slice(lo, i + 1);
      const values = {};
      for (const a of axes) {
        const vs = win.map(w => w[a.key]).filter(v => v != null);
        values[a.key] = vs.length ? vs.reduce((s, v) => s + v, 0) / vs.length : null;
      }
      return { date: d.date, n_passes: d.n_passes, session_id: d.session_id, values };
    });
  }, [data, axes, mode]);

  // Period-average shape — the static reference outline.
  const reference = useMemo(() => {
    const values = {};
    for (const a of axes) {
      const vs = (data || []).map(d => d[a.key]).filter(v => v != null);
      values[a.key] = vs.length ? vs.reduce((s, v) => s + v, 0) / vs.length : null;
    }
    return values;
  }, [data, axes]);

  // Clamp the frame index when the Period slider shrinks the window.
  useEffect(() => {
    setIdx(i => Math.min(i, Math.max(0, frames.length - 1)));
  }, [frames.length]);

  // Playback: one frame per tick, stop (don't loop) at the end.
  useEffect(() => {
    if (!playing) return;
    const t = setInterval(() => {
      setIdx(i => {
        if (i >= frames.length - 1) { setPlaying(false); return i; }
        return i + 1;
      });
    }, RADAR_FRAME_MS);
    return () => clearInterval(t);
  }, [playing, frames.length]);

  if (frames.length < 2) return null;   // nothing to play
  const frame = frames[Math.min(idx, frames.length - 1)];
  const frameCaption = [
    frame.date || '—',
    lapBySession?.[frame.session_id] != null
      ? `best ${fmtLap(lapBySession[frame.session_id])}` : null,
    frame.n_passes != null ? `${frame.n_passes} passes` : null,
  ].filter(Boolean).join(' · ');

  const chartData = axes.map(a => ({
    axis: RADAR_SHORT[a.key] || a.label,
    key: a.key,
    label: a.label,
    now: frame.values[a.key],
    ref: reference[a.key],
    best: bestLapFrame?.values?.[a.key] ?? null,
  }));

  return (
    <div className={styles.chartWrap}>
      <div className={styles.chartTitle}>
        Style radar over time
        <InfoDot text={TIPS.styleRadar} />
        <span className={styles.chartHint}>
          · percentile vs {styleTrend?.field_size ?? '—'} drivers — bigger = more of each trait
        </span>
      </div>
      <div className={styles.radarControls}>
        <button className={styles.radarPlay}
                onClick={() => {
                  if (!playing && idx >= frames.length - 1) setIdx(0);
                  setPlaying(p => !p);
                }}
                aria-label={playing ? 'Pause' : 'Play'}
                title={playing ? 'Pause' : 'Play the sessions in order'}>
          {playing ? '⏸' : '⏵'}
        </button>
        <input type="range" className={styles.radarScrub}
               min={0} max={frames.length - 1}
               value={Math.min(idx, frames.length - 1)}
               onChange={e => { setPlaying(false); setIdx(Number(e.target.value)); }}
               aria-label="Session" />
        <span className={styles.radarCaption} title={frameCaption}>{frameCaption}</span>
        {bestLapFrame && (
          <label className={styles.radarBestToggle}
                 title={`Overlay the technique profile from your ${fmtLap(bestLapFrame.lapTime)} best lap session`}>
            <input type="checkbox" checked={showBestLap}
                   onChange={e => setShowBestLap(e.target.checked)} />
            <span className={styles.radarBestSwatch} aria-hidden="true" />
            Best lap {fmtLap(bestLapFrame.lapTime)}
          </label>
        )}
        <span className={styles.seg} role="group" aria-label="Radar frame basis">
          {[['session', 'Session'], ['form', 'Form'], ['todate', 'To date']].map(([val, label]) => (
            <button key={val}
                    className={mode === val ? `${styles.segOn} ${styles.radarModeOn}` : styles.segBtn}
                    onClick={() => setMode(val)}
                    title={val === 'session' ? 'Each frame = that session alone (noisier)'
                         : val === 'form' ? `Each frame = your last ${RADAR_FORM_N} sessions averaged (recent form)`
                         : 'Each frame = everything up to that date (accumulated fingerprint)'}>
              {label}
            </button>
          ))}
        </span>
      </div>
      <div ref={radarTooltip.containerRef} style={{ width: '100%', height: 330 }}>
      <ResponsiveContainer width="100%" height="100%">
        <RadarChart data={chartData} accessibilityLayer={false}
                    onMouseMove={radarTooltip.onMouseMove}
                    margin={{ top: 8, right: 30, bottom: 8, left: 30 }}>
          <PolarGrid stroke={chartColors.grid} />
          <PolarAngleAxis dataKey="axis" tick={{ fill: chartColors.tick, fontSize: 11 }} />
          <PolarRadiusAxis domain={[0, 100]} tick={{ fill: chartColors.tick, fontSize: 9 }} tickCount={5} />
          <Tooltip content={<RadarAxisTip />}
                   position={radarTooltip.position}
                   wrapperStyle={radarTooltip.wrapperStyle} />
          <Radar name="Period average" dataKey="ref"
                 stroke={chartColors.tick} strokeDasharray="4 3" strokeWidth={1.2}
                 fill={chartColors.tick} fillOpacity={0}
                 connectNulls isAnimationActive={false} />
          <Radar name={frame.date || 'Current'} dataKey="now"
                 stroke="#60a5fa" strokeWidth={2.2} fill="#60a5fa" fillOpacity={0.25}
                 connectNulls isAnimationActive
                 animationDuration={450} animationEasing="linear" />
          {showBestLap && bestLapFrame && (
            <Radar name={`Best lap ${fmtLap(bestLapFrame.lapTime)}`} dataKey="best"
                   stroke="#f59e0b" strokeWidth={2.2}
                   fill="#f59e0b" fillOpacity={0.08}
                   connectNulls isAnimationActive={false} />
          )}
        </RadarChart>
      </ResponsiveContainer>
      </div>
    </div>
  );
}

// A simple multi-line "metric over sessions" trend chart, used for the
// driving-style / grip / consistency / per-corner progression graphs. `lines` is
// [{key,name,color}]; `valueFormat` formats tooltip + Y-axis values. `tip` adds an
// explanatory hover next to the title; `detail(dataKey, datum)` adds a per-point
// suffix in the tooltip (e.g. coasting → seconds). Renders nothing if no point
// has any of the requested values.
function TrendChart({
  title, hint, tip, data, lines, height = 280, yDomain,
  valueFormat, yTickFormat, yUnit, anchoredTooltip = false,
  onPointClick, header, detail,
}) {
  const { chartColors } = useTheme();
  const edgeTooltip = useChartEdgeTooltip(anchoredTooltip, 190);
  const hasData = data && data.some(d => lines.some(l => d[l.key] != null));
  if (!hasData) return null;
  const fmt = valueFormat || (v => v);
  return (
    <div className={styles.chartWrap}>
      <div className={styles.chartTitle}>
        {title}
        <InfoDot text={tip} />
        {hint && <span className={styles.chartHint}>{hint}</span>}
        {header}
      </div>
      <div ref={edgeTooltip.containerRef} style={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} onClick={onPointClick ? (e) => onPointClick(e, data) : undefined}
                   accessibilityLayer={false}
                   onMouseMove={edgeTooltip.onMouseMove}
                   margin={{ top: 8, right: 8, left: yUnit ? 8 : 0, bottom: 0 }}
                   style={onPointClick ? { cursor: 'pointer' } : undefined}>
          <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
          {/* Unique row index as category key (event_date repeats → duplicate
              band categories break hover mapping); format back to date. */}
          <XAxis dataKey="i" type="category"
                 tickFormatter={(i) => data[i]?.date ?? ''}
                 tick={{ fill: chartColors.tick, fontSize: 11 }} />
          <YAxis domain={yDomain || ['auto', 'auto']} tickFormatter={yTickFormat || fmt}
                 width={yUnit ? 58 : 48}
                 tick={{ fill: chartColors.tick, fontSize: 11 }}
                 label={yUnit ? {
                   value: yUnit,
                   angle: -90,
                   position: 'insideLeft',
                   fill: chartColors.tick,
                   fontSize: 11,
                 } : undefined} />
          <Tooltip contentStyle={{ background: 'var(--surface, #1a1d26)', border: '1px solid var(--border, #2a2f3a)', color: 'var(--text, #e5e7eb)' }}
                   position={edgeTooltip.position}
                   wrapperStyle={edgeTooltip.wrapperStyle}
                   labelFormatter={(i) => data[i]?.date ?? ''}
                   formatter={(v, n, item) => {
                     const base = fmt(v);
                     const extra = detail ? detail(item?.dataKey, item?.payload) : null;
                     return [extra ? `${base} · ${extra}` : base, item?.name ?? n];
                   }} />
          {lines.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
          {lines.map(l => (
            <Line key={l.key} type="monotone" dataKey={l.key} name={l.name} stroke={l.color}
                  strokeWidth={2} dot={false} connectNulls />
          ))}
        </LineChart>
      </ResponsiveContainer>
      </div>
    </div>
  );
}

// Progression chart tooltip — best lap, driver score, and the session's weather.
function ProgressTooltip({ active, payload, unitPrefs }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  const prefs = unitPrefs || { temp: 'F', speed: 'mph', pressure: 'psi' };
  const w = d.weather ? convertWeatherUnits(d.weather, prefs) : null;
  const wetTag = d.is_wet === true ? 'Wet' : d.is_wet === false ? 'Dry' : null;
  return (
    <div className={styles.tooltip}>
      <div className={styles.tooltipDate}>{d.date}</div>
      {d.best != null && <div>Best lap: <strong>{fmtLap(d.best)}</strong></div>}
      {d.median != null && <div>Typical lap: <strong>{fmtLap(d.median)}</strong></div>}
      {d.score != null && <div>Score: <strong>{Math.round(d.score)}</strong></div>}
      <div className={styles.tooltipWx}>
        {w ? (
          <>
            <div className={styles.tooltipWxHead}>
              <strong>{w.condition}</strong>
              {w.temp && <span> · {w.temp}</span>}
              {wetTag && <span className={styles.tooltipWetTag}> · {wetTag}</span>}
            </div>
            <div className={styles.tooltipWxRows}>
              {w.wind && <span>Wind {w.wind}{w.windDir ? ` ${w.windDir}` : ''}</span>}
              {w.humidity && <span>Humidity {w.humidity}</span>}
              {d.weather.precipitation_mm > 0 && w.precip && <span>Precip {w.precip}</span>}
              {w.pressure && <span>Pressure {w.pressure}</span>}
            </div>
          </>
        ) : (
          <span className={styles.tooltipMuted}>Weather unavailable</span>
        )}
      </div>
    </div>
  );
}

function Tile({ label, value }) {
  return (
    <div className={styles.tile}>
      <div className={styles.tileValue}>{value}</div>
      <div className={styles.tileLabel}>{label}</div>
    </div>
  );
}

function TechCell({ label, own, field, higherBetter = false }) {
  const delta = (own != null && field != null) ? own - field : null;
  // "Behind" = own worse than field. higherBetter: bigger own is better.
  const behind = delta == null ? false : (higherBetter ? delta < 0 : delta > 0);
  return (
    <div className={styles.techCell}>
      <div className={styles.techLabel}>{label}</div>
      <div className={styles.techVals}>
        <span>You: <strong>{own != null ? own.toFixed(0) : '—'}</strong></span>
        <span className={styles.muted}>Field: {field != null ? field.toFixed(0) : '—'}</span>
        {delta != null && (
          <span className={behind ? styles.gapBad : styles.gapGood}>
            {delta > 0 ? '+' : ''}{delta.toFixed(0)}m
          </span>
        )}
      </div>
    </div>
  );
}
